import { Panel } from "components/Panel/Panel";
import { changeGroupJoinType, deleteGroup, leaveGroup } from "firebase-config";
import { useFetchUsernames } from "hooks/useFetchUsernames";
import {
  getGroupRegularUserIds,
  Group,
  groupApproveRequest,
} from "model/Group/Group";
import { pickemPath } from "pages/PageConfigs";
import { ColumnContainer, GrowColumn } from "pages/styles";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { selectAllUsernamesFetched, selectUid } from "selectors/User";
import { ErrorText } from "styles/text";
import {
  JoinTypeClosed,
  JoinTypeOpen,
  JoinTypeRequest,
} from "TrackmaniaDetails";
import { ConfirmButton } from "../../ConfirmButton/ConfirmButton";
import { InProgressSpinner } from "../CreateGroup/styles";
import { GroupMember } from "./GroupMember";
import {
  GroupActionsContainer,
  JoinTypeContainer,
  JoinTypeSelect,
  JoinTypeText,
  MembersContainer,
} from "./styles";
import { useAppSelector } from "app/hooks";

interface Props {
  group: Group;
  groupId: string;
}

export const GroupMembers = ({ group, groupId }: Props) => {
  const navigate = useNavigate();
  const callerUid = useAppSelector(selectUid);
  const [groupActionErrorText, setGroupActionErrorText] = useState("");
  const [selectedJoinType, setSelectedJoinType] = useState<string>(
    group.joinType
  );
  const [selectedJoinTypeChanging, setSelectedJoinTypeChanging] =
    useState<boolean>(false);

  var callerType = "";
  if (group.ownerUid === callerUid) {
    callerType = "Owner";
  } else if (group.adminUids?.includes(callerUid!) ?? false) {
    callerType = "Admin";
  } else {
    callerType = "Member";
  }

  var uidsToFetch = group.memberUids ?? [];
  if (callerType === "Owner" || callerType === "Admin") {
    uidsToFetch = uidsToFetch
      .concat(group.bannedUids ?? [])
      .concat(group.requestUids ?? []);
  }
  useFetchUsernames(uidsToFetch);
  const membersFetched = useAppSelector((state) =>
    selectAllUsernamesFetched(state, group.memberUids ?? [])
  );
  const bannedUsersFetched = useAppSelector((state) =>
    selectAllUsernamesFetched(state, group.bannedUids ?? [])
  );
  const requestsFetched = useAppSelector((state) =>
    selectAllUsernamesFetched(state, group.requestUids ?? [])
  );

  const onSelectedJoinTypeChange = (newValue: string) => {
    setSelectedJoinTypeChanging(true);
    changeGroupJoinType({ joinType: newValue, groupId: groupId })
      .then(() => {
        group.joinType = newValue;
        setSelectedJoinType(newValue);
        if (newValue === JoinTypeOpen) {
          group.requestUids?.forEach((uid) => groupApproveRequest(group, uid));
        }
      })
      .catch((error) => {
        setGroupActionErrorText(error.message);
      })
      .finally(() => setSelectedJoinTypeChanging(false));
  };

  const onLeaveGroup = () => {
    return leaveGroup({ groupId: groupId })
      .then(() => navigate(pickemPath))
      .catch((error) => {
        setGroupActionErrorText(error.message);
      });
  };

  const onDeleteGroup = () => {
    return deleteGroup({ groupId: groupId })
      .then(() => navigate(pickemPath))
      .catch((error) => {
        setGroupActionErrorText(error.message);
      });
  };

  const groupActions = getGroupActions(callerType, onLeaveGroup, onDeleteGroup);
  return (
    <ColumnContainer>
      <GrowColumn>
        <GroupActionsContainer>
          {callerType === "Owner" && (
            <JoinTypeContainer>
              <JoinTypeText>Join Type</JoinTypeText>
              <JoinTypeSelect
                disabled={selectedJoinTypeChanging}
                value={selectedJoinType}
                onChange={(e) => {
                  onSelectedJoinTypeChange(e.target.value);
                }}
              >
                <option value={JoinTypeOpen}>{JoinTypeOpen}</option>
                <option value={JoinTypeRequest}>{JoinTypeRequest}</option>
                <option value={JoinTypeClosed}>{JoinTypeClosed}</option>
              </JoinTypeSelect>
              {selectedJoinTypeChanging && <InProgressSpinner />}
            </JoinTypeContainer>
          )}
          <ErrorText>{groupActionErrorText}</ErrorText>
          <div style={{ marginLeft: "auto" }}>{groupActions}</div>
        </GroupActionsContainer>
        {(callerType === "Admin" || callerType === "Owner") &&
          (group.requestUids?.length ?? 0) > 0 && (
            <Panel header="Requests" loading={requestsFetched}>
              <MembersContainer>
                {group.requestUids!.map((uid) =>
                  mapUidToJsx(groupId, group, uid, "Request", callerType)
                )}
              </MembersContainer>
            </Panel>
          )}
        <Panel loading={!membersFetched}>
          <MembersContainer>
            {mapUidToJsx(groupId, group, group.ownerUid, "Owner", callerType)}
            {group.adminUids?.map((uid) =>
              mapUidToJsx(groupId, group, uid, "Admin", callerType)
            )}
            {getGroupRegularUserIds(group)?.map((uid) =>
              mapUidToJsx(groupId, group, uid, "Member", callerType)
            )}
          </MembersContainer>
        </Panel>
        {(callerType === "Admin" || callerType === "Owner") &&
          (group.bannedUids?.length ?? 0) > 0 && (
            <Panel header="Banned Users" loading={bannedUsersFetched}>
              <MembersContainer>
                {group.bannedUids!.map((uid) =>
                  mapUidToJsx(groupId, group, uid, "Banned", callerType)
                )}
              </MembersContainer>
            </Panel>
          )}
      </GrowColumn>
    </ColumnContainer>
  );
};

const mapUidToJsx = (
  groupId: string,
  group: Group,
  uid: string,
  userType: string,
  callerType: string
) => {
  return (
    <GroupMember
      key={uid}
      groupId={groupId}
      group={group}
      uid={uid}
      initialUserType={userType}
      callerType={callerType}
    />
  );
};

const getGroupActions = (
  userType: string,
  onLeaveGroup: () => Promise<void>,
  onDeleteGroup: () => Promise<void>
) => {
  if (userType === "Owner") {
    return [
      <ConfirmButton
        key={"Delete"}
        confirmFunction={onDeleteGroup}
        buttonText="Delete Group"
        confirmWarning="Deleting a group is irreversible, are you sure?"
      />,
    ];
  }

  return [
    <ConfirmButton
      key={"Leave"}
      confirmFunction={onLeaveGroup}
      buttonText="Leave Group"
      confirmWarning="Are you sure you want to leave the group?"
    />,
  ];
};
